/**
 * Notes:
 * We don't use a portal to render the overlay because we want this to also work for elements
 * that aren't at the root of the page. This does introduce some major complexity in handling
 * the stacking context, which may leave some elements on top of the overlay. For now the advice
 * is to not use this component unless you are at the root (e.g. modals, layout) to avoid this. In
 * the future I believe I can use a global z-index to detect which elements won't be obscured and then
 * create a seperate overlay over them as well.
 */

import { useContext } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { HTMLMotionProps } from 'motion/react';

import { getOrCreateContext } from '@context/singleton-manager';
import useIsomorphicLayoutEffect from '@hooks/use-isomorphic-layout-effect';

import styles from './Overlay.module.scss';

interface OverlayProps extends HTMLMotionProps<'div'> {
  show: boolean;
  id: string;
  onClick: (event: React.MouseEvent) => void;
}

const overlayContext = getOrCreateContext('hover');

const Overlay: React.FC<React.PropsWithChildren<OverlayProps>> = ({
  show,
  id,
  onClick,
  children,
  className,
  ...rest
}) => {
  const { activeId, requestToMakeActive, makeInactive } = useContext(overlayContext);
  const isVisible = activeId === id;

  function handleKeyDown(event: React.KeyboardEvent) {
    switch (event.key) {
      case 'Escape': {
        if (isVisible) makeInactive(id);
      }
    }
  }

  useIsomorphicLayoutEffect(() => {
    if (show) requestToMakeActive(id);
    else makeInactive(id);
  }, [show]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="overlay"
          className={className ? styles['overlay'] + ' ' + className : styles['overlay']}
          aria-hidden={!isVisible}
          layoutId="overlay"
          onClickCapture={onClick}
          onKeyDownCapture={handleKeyDown}
          {...rest}
        ></motion.div>
      )}
      <div className={styles['focused-content']} key="focus-content" data-show-overlay={isVisible}>
        {children}
      </div>
    </AnimatePresence>
  );
};

export default Overlay;
